import React from 'react';
import { Box } from '../Box';
import { Typography } from '../Typography';
import { Tooltip } from '../Tooltip';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCircleExclamation,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';

interface EnrichingCodesInfoProps {
  iconOnly?: boolean;
  isEncounter?: boolean;
  dataCoverage?: (string | undefined)[];
}

interface StatusInfoType {
  text: string;
  icon: any;
  color: string;
  description: string;
}

// Helper component to render a status item
const StatusItem: React.FC<{ status: StatusInfoType }> = ({ status }) => (
  <>
    <Box className="flex !items-center gap-2 flex-row">
      <Icon icon={status.icon} size="lg" className={status.color} />
      <Typography className="!text-xs text-white font-semibold">
        Status:
      </Typography>
      <Typography className="!text-xs text-white font-normal">
        {status.text}
      </Typography>
    </Box>
    <Box className="flex flex-col gap-3 pl-6">
      <Typography className="!text-xs text-white font-normal">
        {status.description}
      </Typography>
    </Box>
  </>
);

const EnrichingCodesInfo: React.FC<EnrichingCodesInfoProps> = ({
  iconOnly = false,
  isEncounter = false,
  dataCoverage,
}) => {
  // get all status information
  const getStatusInfo = (status: string, isAllEnriched = false) => {
    switch (status) {
      case 'ENRICHED':
        return {
          text: 'Up to Date',
          icon: faCircleCheck,
          color: 'text-green-500',
          description: `${
            isAllEnriched ? 'All' : 'Some'
          } codes in this search are using the latest available data from commercial payers and Medicare FFS.`,
        };
      case 'REQUESTED':
        return {
          text: 'Enriching',
          icon: faCircleExclamation,
          color: 'text-yellow-500',
          description:
            'Some codes in this set are being enriched with the latest Medicare FFS data. During this process, Medicare coverage values may appear lower than expected.',
        };
      case 'NOT_ENRICHED':
        return {
          text: 'Limited FFS Data Set',
          icon: faCircleExclamation,
          color: 'text-yellow-500',
          description:
            'Some codes in this set include a limited set of Medicare FFS data. Medicare FFS coverage values may appear lower than expected.',
        };
      case 'NOT_ENRICHED_ENCOUNTER':
        return {
          text: 'Limited FFS Data Set',
          icon: faCircleExclamation,
          color: 'text-yellow-500',
          description:
            'Encounter searches include a limited set of Medicare FFS data. Medicare FFS coverage values may appear lower than expected.',
        };
      default:
        return {
          text: 'Unknown',
          icon: faCircleExclamation,
          color: 'text-yellow-500',
          description: 'Data coverage status information unavailable.',
        };
    }
  };

  // Get unique statuses from dataCoverage array
  const getUniqueStatuses = () => {
    if (!dataCoverage || dataCoverage.length === 0) return [];
    return dataCoverage
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  const allComplete = dataCoverage?.every((status) => status === 'ENRICHED');
  const uniqueStatuses = getUniqueStatuses();

  // For the main display, use the most critical status icon
  const mainIcon =
    isEncounter || !allComplete ? faCircleExclamation : faCircleCheck;
  const mainColor =
    isEncounter || !allComplete ? 'text-yellow-500' : 'text-green-500';

  return (
    <div className="flex items-center">
      <Tooltip
        position={'bottom'}
        content={
          <Box className="p-3 flex flex-col gap-3 w-64">
            <Typography className="!text-sm text-white font-bold">
              Data Coverage
            </Typography>

            {isEncounter ? (
              <StatusItem status={getStatusInfo('NOT_ENRICHED_ENCOUNTER')} />
            ) : uniqueStatuses.length > 0 ? (
              uniqueStatuses.map((status, index) => (
                <StatusItem
                  key={index}
                  status={getStatusInfo(status, allComplete)}
                />
              ))
            ) : (
              <StatusItem status={getStatusInfo('')} />
            )}
          </Box>
        }
      >
        <Box className="flex items-center gap-2">
          <Icon icon={mainIcon} className={`w-4 h-4 ${mainColor}`} />
          {!iconOnly && (
            <Typography className="text-md font-normal whitespace-nowrap">
              Data Coverage
            </Typography>
          )}
        </Box>
      </Tooltip>
    </div>
  );
};

export default EnrichingCodesInfo;
